import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import ScheduleGrid from "../components/ScheduleGrid";
import { apiClient } from "../services/api";
import { ClassData, DEFAULT_SCHEDULE_CONFIG, computeHourRange } from "../types/schedule";

function ScheduleAllPage() {
  const [classes, setClasses] = useState<ClassData[]>([]);
  const [teachers, setTeachers] = useState<string[]>([]);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const navigate = useNavigate();

  const config = { ...DEFAULT_SCHEDULE_CONFIG, hourHeight: 25 };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (classes.length === 0) return;
    const uniqueTeachers = Array.from(new Set(classes.map((c) => c.teacher)));
    setTeachers(uniqueTeachers.sort());
  }, [classes]);

  const fetchClasses = async () => {
    try {
      const data = await apiClient.getClasses();
      setClasses(data);
    } catch (error) {
      console.error("Failed to fetch classes:", error);
    }
  };

  const isClassInWeek = (cls: ClassData, weekOffset: number): boolean => {
    const today = new Date();
    const monday = new Date(today);
    const day = today.getDay();
    monday.setDate(today.getDate() - day + (day === 0 ? -6 : 1) + weekOffset * 7);
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    if (cls.classCategory === "special" && cls.classStartDate && cls.classEndDate) {
      const start = new Date(cls.classStartDate);
      const end = new Date(cls.classEndDate);
      return !(end < monday || start > sunday);
    }
    if (cls.classCategory === "supplement" && cls.classStartDate) {
      const supplementDate = new Date(cls.classStartDate);
      return supplementDate >= monday && supplementDate <= sunday;
    }
    return true;
  };

  const filteredByWeek = classes.filter((cls) => isClassInWeek(cls, 0));

  const gridsToRender = teachers.map((teacher) => ({
    teacher,
    classes: filteredByWeek.filter((c) => c.teacher === teacher),
  }));

  // 각 선생님 그리드의 예상 높이 계산 → 높이 합이 최소화되는 split point 탐색
  const gridHeights = gridsToRender.map(({ classes: tc }) => {
    const { minHour, maxHour } = computeHourRange(tc, config);
    return (maxHour - minHour + 1) * config.hourHeight + 40;
  });
  let bestSplit = Math.ceil(gridsToRender.length / 2);
  let bestDiff = Infinity;
  for (let k = 1; k < gridsToRender.length; k++) {
    const h1 = gridHeights.slice(0, k).reduce((a, b) => a + b, 0);
    const h2 = gridHeights.slice(k).reduce((a, b) => a + b, 0);
    const diff = Math.abs(h1 - h2);
    if (diff < bestDiff) { bestDiff = diff; bestSplit = k; }
  }
  const leftColumn = gridsToRender.slice(0, bestSplit);
  const rightColumn = gridsToRender.slice(bestSplit);

  const renderColumn = (items: typeof gridsToRender) => (
    <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 6 }}>
      {items.map(({ teacher, classes: teacherClasses }) => (
        <ScheduleGrid
          key={teacher}
          title={teacher}
          classes={teacherClasses}
          config={config}
          variant="compact"
          hoveredKey={hoveredKey}
          setHoveredKey={setHoveredKey}
          onClassClick={(classId) => navigate(`/class/${classId}`)}
        />
      ))}
    </div>
  );

  return (
    <PageLayout title="시간표 일람">
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {renderColumn(leftColumn)}
        {renderColumn(rightColumn)}
      </div>
    </PageLayout>
  );
}

export default ScheduleAllPage;
